/**
 * Speaker utilities — shared logic for resolving Q/A speakers in transcripts.
 *
 * Extracted from TranscriptView so MetadataPalette and other components
 * can match person names to speaker lines without duplicating logic.
 *
 * Two main functions:
 *   - buildSpeakerMap: maps line indices → {q, a} speaker names
 *   - matchPersonToSpeaker: fuzzy-matches a person name to a speaker name
 */

import type {ExaminationBlock} from '../../../../../schemas';
import type {MatchRange} from './useLineSpotlight';

// ── Name matching helpers ────────────────────────────────────────────────

/** Strip common honorifics, professional titles, and prefixes from a name for comparison.
 *  Handles legal, law enforcement, military, medical, and academic titles. */
export function stripHonorific(s: string): string {
    // Iteratively strip — handles "Det. Sgt. Smith" (multiple prefixes)
    let result = s.toLowerCase().trim();
    const TITLE_RE = /^(mr|mrs|ms|dr|judge|hon|attorney|atty|counselor|esq|detective|det|officer|ofc|agent|special agent|sa|sgt|sergeant|lt|lieutenant|cpl|corporal|capt|captain|chief|commander|cmdr|prof|professor|reverend|rev|sr|jr|ii|iii)\.?\s*/i;
    let prev = '';
    while (result !== prev) {
        prev = result;
        result = result.replace(TITLE_RE, '').trim();
    }
    return result;
}

/** Extract the last name from a (possibly honorific-prefixed) name. */
export function lastName(s: string): string {
    const parts = stripHonorific(s).split(/\s+/);
    return parts[parts.length - 1] || '';
}

/**
 * Fuzzy-match a person name (from summary/entity) against a speaker name
 * (from examination block). Handles honorific differences, last-name-only
 * references, and substring containment.
 *
 * Examples:
 *   "Joseph Nadeau" vs "Nadeau" → true (spotlight contains speaker last name)
 *   "Joseph Nadeau" vs "MR. NADEAU" → true (last name match after stripping)
 *   "John Smith" vs "MR. SMITH" → true (last name match)
 *   "Dr. Sarah Chen" vs "Sarah Chen" → true (honorific stripped)
 */
/**
 * Fuzzy-match a person name (from summary/entity) against a speaker name
 * (from examination block). Handles honorific/title differences, last-name-only
 * references, substring containment, and word overlap.
 *
 * Examples:
 *   "Peter Paul" vs "Detective Peter Paul" → true (title stripped)
 *   "Joseph Nadeau" vs "MR. NADEAU" → true (last name match)
 *   "John Smith" vs "Det. Sgt. Smith" → true (last name match after stripping)
 *   "Dr. Sarah Chen" vs "Sarah Chen" → true (title stripped)
 *   "Peter Paul" vs "Paul" → true (substring containment)
 */
export function namesMatch(personName: string, speakerName: string): boolean {
    const aLower = personName.toLowerCase().trim();
    const bLower = speakerName.toLowerCase().trim();
    if (aLower === bLower) return true;

    const aStripped = stripHonorific(personName);
    const bStripped = stripHonorific(speakerName);
    if (aStripped === bStripped) return true;

    // Last name match (requires >= 3 chars to avoid false positives)
    const aLast = lastName(personName);
    const bLast = lastName(speakerName);
    if (aLast.length >= 3 && aLast === bLast) return true;

    // Substring containment (one contains the other after stripping)
    if (aStripped.length >= 3 && bStripped.includes(aStripped)) return true;
    if (bStripped.length >= 3 && aStripped.includes(bStripped)) return true;

    // Word overlap: if all significant words (≥3 chars) from the shorter name
    // appear in the longer name, it's likely the same person.
    // Handles: "Peter Paul" vs "Peter J. Paul" or "Paul, Peter"
    const aWords = aStripped.split(/\s+/).filter(w => w.length >= 3);
    const bWords = bStripped.split(/\s+/).filter(w => w.length >= 3);
    if (aWords.length > 0 && bWords.length > 0) {
        const shorter = aWords.length <= bWords.length ? aWords : bWords;
        const longer = aWords.length <= bWords.length ? bWords : aWords;
        const allMatch = shorter.every(w => longer.some(lw => lw === w || lw.includes(w) || w.includes(lw)));
        if (allMatch && shorter.length >= 1) return true;
    }

    return false;
}

// ── Speaker map ──────────────────────────────────────────────────────────

export interface SpeakerNames {
    /** Resolved name for 'Q' speaker (examining attorney) */
    q: string | null;
    /** Resolved name for 'A' speaker (witness) */
    a: string | null;
}

/** Minimal line info needed for speaker mapping (avoids coupling to TranscriptLine). */
export interface SpeakerLineInfo {
    speaker: string | null;
    isPageBreak: boolean;
    content: string;
}

/**
 * Build a map from line index → resolved speaker names.
 * Tracks the current transcript page via page break markers,
 * then looks up which examination block covers that page.
 *
 * Uses transcript_page_start (not pdf_page_start) because page break
 * markers in the OCR text are transcript page numbers.
 */
export function buildSpeakerMap(
    lines: SpeakerLineInfo[],
    blocks: ExaminationBlock[] | undefined,
    deponentName: string | undefined,
): Map<number, SpeakerNames> {
    const map = new Map<number, SpeakerNames>();
    if (!blocks || blocks.length === 0) return map;

    const sorted = blocks
        .slice()
        .sort((a, b) =>
            (a.transcript_page_start ?? 0) - (b.transcript_page_start ?? 0)
        );

    function findBlock(transcriptPage: number): ExaminationBlock | undefined {
        for (let i = sorted.length - 1; i >= 0; i--) {
            const start = sorted[i].transcript_page_start ?? 0;
            if (start <= transcriptPage) return sorted[i];
        }
        return sorted[0];
    }

    let currentPage = 1;
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (line.isPageBreak && /^\d+$/.test(line.content)) {
            currentPage = parseInt(line.content, 10);
            continue;
        }
        if (!line.speaker) continue;
        if (line.speaker !== 'Q' && line.speaker !== 'A') continue;

        const block = findBlock(currentPage);
        if (block) {
            map.set(i, {
                q: block.attorney ?? null,
                a: block.witness ?? deponentName ?? null,
            });
        }
    }
    return map;
}

// ── Speaker line ranges ──────────────────────────────────────────────────

/**
 * Build speaker-line override ranges for spotlight matching.
 *
 * Given a speaker map and a list of visible spotlight texts, returns a map
 * from spotlight text → line ranges where that person speaks. Uses fuzzy
 * name matching so "Joseph Nadeau" matches examination block "MR. NADEAU".
 *
 * Also collects all unique speaker names found — useful for MetadataPalette
 * to provide altTexts on the spotlight.
 */
export function buildSpeakerOverrides(
    lines: SpeakerLineInfo[],
    speakerMap: Map<number, SpeakerNames>,
    spotlightTexts: string[],
): Map<string, MatchRange[]> {
    const overrides = new Map<string, MatchRange[]>();
    if (speakerMap.size === 0 || spotlightTexts.length === 0) return overrides;

    // Build: resolved speaker name → list of line indices where they speak
    const tempMap = new Map<string, number[]>();
    for (const [lineIdx, names] of speakerMap) {
        const line = lines[lineIdx];
        if (!line?.speaker) continue;
        const resolvedName = line.speaker === 'Q' ? names.q : line.speaker === 'A' ? names.a : null;
        if (!resolvedName) continue;
        const key = resolvedName.toLowerCase();
        if (!tempMap.has(key)) tempMap.set(key, []);
        tempMap.get(key)!.push(lineIdx);
    }
    const speakerEntries = Array.from(tempMap.entries()).map(([name, lines]) => ({name, lines}));

    for (const text of spotlightTexts) {
        // Skip long texts that can't be person names
        if (text.length > 50) continue;

        for (const entry of speakerEntries) {
            if (namesMatch(text, entry.name)) {
                overrides.set(text, entry.lines.map(l => ({startLine: l, endLine: l})));
                break;
            }
        }
    }

    return overrides;
}

/**
 * Find which speaker name (from examination blocks) a person name matches.
 * Returns the matched speaker name, or null if no match.
 *
 * Used by MetadataPalette to include altTexts on the spotlight request.
 */
export function matchPersonToSpeaker(
    personName: string,
    blocks: ExaminationBlock[] | undefined,
    deponentName: string | undefined,
): {role: 'witness' | 'attorney'; matchedName: string; blocks: ExaminationBlock[]} | null {
    if (!blocks || blocks.length === 0) return null;

    // Collect all unique speaker names from blocks
    const witnessNames = new Set<string>();
    const attorneyNames = new Set<string>();
    for (const block of blocks) {
        if (block.witness) witnessNames.add(block.witness);
        if (block.attorney) attorneyNames.add(block.attorney);
    }
    if (deponentName) witnessNames.add(deponentName);

    // Try witnesses first (more common to click on deponent)
    for (const name of witnessNames) {
        if (namesMatch(personName, name)) {
            const matchedBlocks = blocks.filter(b =>
                (b.witness && namesMatch(personName, b.witness)) ||
                (deponentName && namesMatch(personName, deponentName))
            );
            return {role: 'witness', matchedName: name, blocks: matchedBlocks};
        }
    }

    // Then attorneys
    for (const name of attorneyNames) {
        if (namesMatch(personName, name)) {
            const matchedBlocks = blocks.filter(b =>
                b.attorney && namesMatch(personName, b.attorney)
            );
            return {role: 'attorney', matchedName: name, blocks: matchedBlocks};
        }
    }

    return null;
}

/**
 * Get all unique speaker names from examination blocks (for altTexts).
 */
export function getAllSpeakerNames(
    blocks: ExaminationBlock[] | undefined,
    deponentName: string | undefined,
): string[] {
    if (!blocks) return [];
    const names = new Set<string>();
    if (deponentName) names.add(deponentName);
    for (const block of blocks) {
        if (block.witness) names.add(block.witness);
        if (block.attorney) names.add(block.attorney);
    }
    return Array.from(names);
}
